import React, { useMemo } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Button,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { BackButton } from "components/common/Buttons";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getProductOptions } from "features/Products/services/ProductService";
import { getStockLocationOptions } from "features/StockLocations/services/StockLocationService";
import { createStockMovement } from "features/StockMovements/services/StockMovementService";
import {
  StockMovementType,
  StockMovementTypeLabel,
} from "features/StockMovements/constants/stock-movement.constants";
import { validateMovementRules } from "features/StockMovements/utils/stockMovementRules";
import { getDocumentTitle } from "utils/helpers";

const listPath = "/inventory-management/stock-movements";

const StockMovementForm = () => {
  document.title = getDocumentTitle("Record stock movement");
  const navigate = useNavigate();

  const validation = useFormik({
    initialValues: {
      movementType: StockMovementType.PURCHASE,
      productId: "" as number | "",
      quantity: "" as number | "",
      fromLocationId: "" as number | "",
      toLocationId: "" as number | "",
      referenceType: "",
      referenceId: "" as number | "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (values.productId === "" || Number.isNaN(Number(values.productId))) {
        errors.productId = "Product is required";
      }
      if (values.quantity === "" || Number.isNaN(Number(values.quantity))) {
        errors.quantity = "Quantity is required";
      }
      const from =
        values.fromLocationId === "" ? null : Number(values.fromLocationId);
      const to =
        values.toLocationId === "" ? null : Number(values.toLocationId);
      try {
        validateMovementRules(
          values.movementType,
          Number(values.quantity),
          from,
          to,
        );
      } catch (e) {
        errors._general = (e as Error).message;
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const res = await createStockMovement({
          productId: Number(values.productId),
          quantity: Number(values.quantity),
          movementType: values.movementType,
          fromLocationId:
            values.fromLocationId === ""
              ? null
              : Number(values.fromLocationId),
          toLocationId:
            values.toLocationId === "" ? null : Number(values.toLocationId),
          referenceType: values.referenceType.trim() || null,
          referenceId:
            values.referenceId === "" ? null : Number(values.referenceId),
        });
        if (res.success) {
          toast.success(res.message);
          navigate(listPath);
        }
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ??
            (err as Error)?.message ??
            "Save failed",
        );
      }
    },
  });

  const { movementType } = validation.values;

  const locationHints = useMemo(() => {
    switch (movementType) {
      case StockMovementType.PURCHASE:
        return { showFrom: false, showTo: true, qtyHint: "Positive (stock in)" };
      case StockMovementType.ISSUE:
        return {
          showFrom: true,
          showTo: false,
          qtyHint: "Negative (e.g. -5 for stock out)",
        };
      case StockMovementType.TRANSFER:
        return {
          showFrom: true,
          showTo: true,
          qtyHint: "Positive units moving between locations",
        };
      case StockMovementType.RETURN:
        return { showFrom: false, showTo: true, qtyHint: "Positive" };
      case StockMovementType.ADJUSTMENT:
        return {
          showFrom: true,
          showTo: true,
          qtyHint: "Fill exactly one location (from XOR to)",
        };
      default:
        return { showFrom: false, showTo: false, qtyHint: "" };
    }
  }, [movementType]);

  const onMovementTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validation.handleChange(e);
    validation.setFieldValue("fromLocationId", "");
    validation.setFieldValue("toLocationId", "");
  };

  const generalError = (validation.errors as Record<string, string | undefined>)
    ._general;

  const showLocationSection = locationHints.showFrom || locationHints.showTo;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Record stock movement"
            pageTitle="Inventory Management"
            items={[{ label: "Stock movements", path: listPath }]}
          />
          <Form
            className="needs-validation"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            <Row className="justify-content-center">
              <Col xl={9} lg={10}>
                <Card className="shadow-none border">
                  <CardHeader className="border-bottom bg-transparent pb-3">
                    <h5 className="card-title mb-1">Record movement</h5>
                    <p className="text-muted mb-0 small">
                      Choose the movement type, product, and quantity, then set
                      locations as required. Optional reference links this row to
                      a document (e.g. purchase order).
                    </p>
                  </CardHeader>
                  <CardBody className="pt-4">
                    {generalError ? (
                      <Alert color="danger" className="mb-4" role="alert">
                        <i className="ri-error-warning-line me-2 align-middle" />
                        {generalError}
                      </Alert>
                    ) : null}

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                      Movement
                    </div>
                    <Row className="g-3 mb-2">
                      <Col md={6} className="min-w-0">
                        <Label className="form-label" htmlFor="sm-movement-type">
                          Type
                        </Label>
                        <Input
                          id="sm-movement-type"
                          type="select"
                          name="movementType"
                          className="form-select"
                          value={validation.values.movementType}
                          onChange={onMovementTypeChange}
                          onBlur={validation.handleBlur}
                        >
                          {(
                            Object.values(StockMovementType) as StockMovementType[]
                          ).map((t) => (
                            <option key={t} value={t}>
                              {StockMovementTypeLabel[t]}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={6}>
                        <Label className="form-label" htmlFor="sm-quantity">
                          Quantity
                        </Label>
                        <Input
                          id="sm-quantity"
                          type="number"
                          name="quantity"
                          step="any"
                          placeholder="0"
                          value={validation.values.quantity}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={Boolean(
                            validation.touched.quantity &&
                              validation.errors.quantity,
                          )}
                        />
                        {locationHints.qtyHint ? (
                          <small className="text-muted d-block mt-1">
                            {locationHints.qtyHint}
                          </small>
                        ) : null}
                        {validation.touched.quantity &&
                        validation.errors.quantity ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.quantity}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col md={12} className="min-w-0">
                        <Label className="form-label">Product</Label>
                        <AsyncPaginatedSelect
                          fetchOptions={getProductOptions}
                          placeholder="Search product…"
                          value={validation.values.productId}
                          onChange={(val: any) =>
                            validation.setFieldValue(
                              "productId",
                              val?.value ?? "",
                            )
                          }
                          onBlur={() =>
                            validation.setFieldTouched("productId", true)
                          }
                          isInvalid={Boolean(
                            validation.touched.productId &&
                              validation.errors.productId,
                          )}
                        />
                        {validation.touched.productId &&
                        validation.errors.productId ? (
                          <FormFeedback type="invalid" className="d-block">
                            {validation.errors.productId}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>

                    {showLocationSection ? (
                      <>
                        <hr className="my-4 text-muted opacity-25" />
                        <div className="text-uppercase text-muted fw-semibold fs-12 mb-3">
                          Locations
                        </div>
                        <Row className="g-3 mb-2">
                          {locationHints.showFrom ? (
                            <Col md={6} className="min-w-0">
                              <Label className="form-label">From location</Label>
                              <AsyncPaginatedSelect
                                fetchOptions={getStockLocationOptions}
                                placeholder="Search location…"
                                value={validation.values.fromLocationId}
                                onChange={(val: any) =>
                                  validation.setFieldValue(
                                    "fromLocationId",
                                    val?.value ?? "",
                                  )
                                }
                                onBlur={() =>
                                  validation.setFieldTouched(
                                    "fromLocationId",
                                    true,
                                  )
                                }
                              />
                            </Col>
                          ) : null}
                          {locationHints.showTo ? (
                            <Col
                              md={locationHints.showFrom ? 6 : 12}
                              className="min-w-0"
                            >
                              <Label className="form-label">To location</Label>
                              <AsyncPaginatedSelect
                                fetchOptions={getStockLocationOptions}
                                placeholder="Search location…"
                                value={validation.values.toLocationId}
                                onChange={(val: any) =>
                                  validation.setFieldValue(
                                    "toLocationId",
                                    val?.value ?? "",
                                  )
                                }
                                onBlur={() =>
                                  validation.setFieldTouched(
                                    "toLocationId",
                                    true,
                                  )
                                }
                              />
                            </Col>
                          ) : null}
                        </Row>
                      </>
                    ) : null}

                    <hr className="my-4 text-muted opacity-25" />

                    <div className="text-uppercase text-muted fw-semibold fs-12 mb-1">
                      Reference
                    </div>
                    <p className="text-muted small mb-3">
                      Optional — link to a source document (type + numeric id).
                    </p>
                    <Row className="g-3 mb-0">
                      <Col md={6}>
                        <Label
                          className="form-label"
                          htmlFor="sm-reference-type"
                        >
                          Reference type
                        </Label>
                        <Input
                          id="sm-reference-type"
                          type="text"
                          name="referenceType"
                          placeholder="e.g. PURCHASE_ORDER"
                          value={validation.values.referenceType}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>
                      <Col md={6}>
                        <Label
                          className="form-label"
                          htmlFor="sm-reference-id"
                        >
                          Reference ID
                        </Label>
                        <Input
                          id="sm-reference-id"
                          type="number"
                          name="referenceId"
                          placeholder="Numeric id"
                          value={validation.values.referenceId}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                        />
                      </Col>
                    </Row>
                  </CardBody>
                  <CardFooter className="bg-light bg-opacity-50 border-top d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      className="btn-label px-4"
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Saving…
                        </>
                      ) : (
                        "Save movement"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            </Row>
          </Form>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default StockMovementForm;
